import { useRef } from "react";
import "./DeclineShareModal.css";
import { useModalA11y } from "./useModalA11y";
import { useI18n } from "../i18n";

/**
 * Confirmation pop-up shown when the resident chooses "No, don't share" on the
 * data-sharing consent (Yvett 17 Jul).
 *
 * It is a MODAL, not a chat turn: it interrupts without advancing the
 * conversation, so "Go back" simply closes it and the consent card is still
 * there to answer again. Only "Continue without sharing" sends the decline.
 *
 * The pop-up restates the safeguard (answers go securely, only to that team) so
 * the resident makes an informed final choice before refusing.
 */
export function DeclineShareModal({
  programme,
  onGoBack,
  onContinue,
  onMessageCoach,
}: {
  /** Display name of the matched programme, e.g. "WorkWell". */
  programme: string;
  /** Close the pop-up and leave the consent question unanswered. */
  onGoBack: () => void;
  /** Confirm the decline — sends "No, don't share" to the engine. */
  onContinue: () => void;
  /** Open the Joy instructions modal (same route as the header button). */
  onMessageCoach: () => void;
}) {
  const { t } = useI18n();
  const dialogRef = useRef<HTMLDivElement>(null);
  // Esc / backdrop click == "Go back": the safer default, since it leaves the
  // resident's choice open rather than confirming a refusal by accident.
  useModalA11y(dialogRef, onGoBack);

  return (
    <div
      className="modal-overlay"
      role="presentation"
      onClick={(e) => e.target === e.currentTarget && onGoBack()}
    >
      <div
        ref={dialogRef}
        className="modal decline-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="decline-modal-title"
      >
        {/* The heading is the dialog's accessible name, but visually it's just
            the first sentence of the message — styling it as a bold violet
            headline made it shout over the actual decision (Naveen 17 Jul), so
            all three sentences read as one flat block of body text. */}
        <h2 className="decline-modal__lead" id="decline-modal-title">
          Without sharing your details, the {programme} team cannot reach you, and the
          session will finish here.
        </h2>
        <p className="decline-modal__text">
          Your answers are sent securely and only the {programme} team can see them.
        </p>
        <p className="decline-modal__text">
          If you'd rather not share them through this chat, you can choose to{" "}
          {/* Inline link, not a button — a third purple button competed with
              "Go back" for attention. It still opens the Joy instructions
              modal (same route as the header button). */}
          <button
            type="button"
            className="decline-modal__coach-link"
            onClick={onMessageCoach}
          >
            send a message to a WorkWell coach
          </button>{" "}
          directly online.
        </p>

        <div className="decline-modal__actions">
          <button type="button" className="decline-modal__back" data-autofocus onClick={onGoBack}>
            {t("decline.goBack")}
          </button>
          <button type="button" className="decline-modal__continue" onClick={onContinue}>
            {t("decline.continue")}
          </button>
        </div>
      </div>
    </div>
  );
}
